import { Currency } from '@backmarket/http-api/src/standards/Currency'
import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'

import type { I18n } from '../types/i18n'

import createCountryFormatter from './formatters/createCountryFormatter'
import createCurrencySymbolFormatter from './formatters/createCurrencySymbolFormatter'
import createDateFormatter from './formatters/createDateFormatter'
import createDistanceFormatter from './formatters/createDistanceFormatter'
import createListFormatter from './formatters/createListFormatter'
import createLocaleFormatter from './formatters/createLocaleFormatter'
import createMessageFormatter from './formatters/createMessageFormatter'
import createNumberFormatter from './formatters/createNumberFormatter'
import createPriceFormatter from './formatters/createPriceFormatter'
import createRelativeDateFormatter from './formatters/createRelativeDateFormatter'

/**
 * @returns A translate function with as attributes plenty of handy transformer functions.
 *
 * @example
 * const i18n = getI18n('en-us', 'USD', false, false, false)
 *
 * i18n({ id: "something", defaultMessage: "the message"}) // 'the message'
 * i18n({ id: "something", defaultMessage: "the message"}, { logMissingTranslation: false }) // 'the message' without logging
 * i18n.language('fr-fr') // "French"
 * i18n.country('FR') // "France"
 * i18n.price({ amount: '10.0', currency: 'EUR' }) // '10.00€'
 * i18n.currencySymbol('EUR') // '€'
 * i18n.date(new Date()) // '01/01/2023'
 * i18n.relativeDate({ year: 12, month: 5, minutes: 2 }) //
 * i18n.distance(1000) //
 * i18n.list(['1', '2', '3']) // "1, 2, 3"
 */
export function getI18n(
  locale: Bcp47Locale,
  currencyCode: Currency,
  showTranslationKeys: boolean,
  showDefaultMessage: boolean,
  shouldLogMissingTranslationValue: boolean,
) {
  const messageFormatter = createMessageFormatter({
    locale,
    showDefaultMessage,
    showTranslationKeys,
    currencyCode,
    shouldLogMissingTranslationValue,
  }) as I18n

  const formatter = messageFormatter
  formatter.date = createDateFormatter(locale)
  formatter.relativeDate = createRelativeDateFormatter(locale)
  formatter.number = createNumberFormatter(locale)
  formatter.country = createCountryFormatter(locale)
  formatter.language = createLocaleFormatter(locale)
  formatter.distance = createDistanceFormatter(locale, messageFormatter)
  formatter.list = createListFormatter(locale)
  formatter.price = createPriceFormatter(locale, currencyCode)
  formatter.currencySymbol = createCurrencySymbolFormatter(locale, currencyCode)
  formatter.currentLocale = locale
  formatter.currentCurrency = currencyCode
  /**
   * @deprecated - use currencySymbol instead
   */
  formatter.currencySign = formatter.currencySymbol

  /**
   * @deprecated - use language instead
   */
  formatter.locale = formatter.language

  return formatter
}
